import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, Clock, ArrowRight } from 'lucide-react';
import Seo, { breadcrumbLd } from '../components/Seo';
import { MaskedLines, FadeUp, Eyebrow } from '../components/motion';
import { useLang, useT } from '../i18n/LangContext';
import { PHONE, PHONE_HREF, EMAIL } from '../data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const t = useT();
  const { lang } = useLang();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, service: 'General enquiry', source: 'contact-page', lang });
      toast.success(t['ct.toastOk']);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch {
      toast.error(t['ct.toastErr']);
    }
    setSending(false);
  };

  return (
    <>
      <Seo title={t['seo.contact.title']} description={t['seo.contact.desc']}
        jsonLd={[breadcrumbLd(['Home', 'Contact'])]} />
      <section className="bg-ink pb-28 pt-16 md:pt-24">
        <div className="container-site grid gap-16 lg:grid-cols-2">
          <div>
            <Eyebrow>{t['ct.eyebrow']}</Eyebrow>
            <MaskedLines as="h1" className="mt-4 font-serif text-4xl font-semibold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
              lines={[{ text: t['ct.h1a'], cls: 'text-white' }, { text: t['ct.h1b'], cls: 'italic text-gold' }]} />
            <FadeUp delay={0.3} className="mt-10 space-y-6">
              <a data-testid="contact-phone" href={PHONE_HREF} className="flex items-center gap-4 text-lg font-bold text-white transition-colors hover:text-gold">
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40"><Phone className="h-5 w-5 text-gold" /></span> {PHONE}
              </a>
              <a data-testid="contact-email" href={`mailto:${EMAIL}`} className="flex items-center gap-4 text-lg font-bold text-white transition-colors hover:text-gold">
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40"><Mail className="h-5 w-5 text-gold" /></span> {EMAIL}
              </a>
              <p className="flex items-center gap-4 text-sm text-silver">
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15"><Clock className="h-5 w-5 text-gold" /></span>
                {t['ct.hours']}
              </p>
              <div className="overflow-hidden rounded-xl border border-white/10">
                <iframe data-testid="contact-map" title={t['ct.mapTitle']}
                  src="https://maps.google.com/maps?q=Orleans%2C%20Ottawa%2C%20ON%2C%20Canada&z=11&output=embed"
                  className="h-56 w-full grayscale-[0.4]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.15}>
            <form data-testid="contact-form" onSubmit={submit} className="rounded-2xl border border-white/10 bg-coal p-8 md:p-10">
              <h2 className="font-serif text-3xl font-semibold">{t['ct.formH2']}</h2>
              <p className="mb-6 mt-2 text-sm text-silver">{t['ct.formSub']}</p>
              <div className="space-y-3">
                <input data-testid="contact-name-input" required placeholder={t['qf.name']} value={form.name} onChange={set('name')} className="input-dark" aria-label={t['qf.name']} />
                <div className="grid gap-3 sm:grid-cols-2">
                  <input data-testid="contact-email-input" required type="email" placeholder={t['qf.email']} value={form.email} onChange={set('email')} className="input-dark" aria-label={t['qf.email']} />
                  <input data-testid="contact-phone-input" required type="tel" placeholder={t['qf.phone']} value={form.phone} onChange={set('phone')} className="input-dark" aria-label={t['qf.phone']} />
                </div>
                <textarea data-testid="contact-message-input" required placeholder={t['ct.msgPh']} rows={5} value={form.message} onChange={set('message')} className="input-dark resize-none" aria-label={t['ct.msgPh']} />
                <button data-testid="contact-submit-button" type="submit" disabled={sending} className="gold-btn w-full disabled:opacity-60">
                  {sending ? t['qf.sending'] : <>{t['ct.send']} <ArrowRight className="h-4 w-4" /></>}
                </button>
              </div>
            </form>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
